import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast from "../../components/CustomComponents/toast/toast.js";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import { logout } from "../../app/auth/authSlice.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import "../compnay/ManageCompany.css";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "-";

const DetailField = ({ label, value, isDarkMode }) => (
  <div
    className="p-3 h-100"
    style={{
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #e5e7eb",
      background: isDarkMode ? "#1f2937" : "#f9fafb",
    }}
  >
    <div
      className="mb-1"
      style={{
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        color: isDarkMode ? "#9ca3af" : "#6b7280",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: "14px",
        fontWeight: 600,
        color: isDarkMode ? "#ffffff" : "#111827",
        wordBreak: "break-word",
      }}
    >
      {value || "-"}
    </div>
  </div>
);

const ManageMarketplaceCustomers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchMarketplaceCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_CUSTOMERS, {
        params: {
          Search: search.trim(),
          PageNumber: pageNumber,
          PageSize: pageSize.value,
        },
      });

      if (response.data?.status) {
        const payload = response.data?.data ?? {};
        const list = Array.isArray(payload.customers) ? payload.customers : [];
        const pagination = payload.pagination ?? {};
        setCustomers(list);
        setTotalRecords(Number(pagination.totalCount) || 0);
        setTotalPages(Number(pagination.totalPages) || 1);
      } else {
        setCustomers([]);
        setTotalRecords(0);
        setTotalPages(1);
        showErrorToast(response.data?.message || "Failed to fetch Go Fatafat customers");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 0);
      } else if (err.response?.status === 403) {
        showErrorToast("You are not authorized to view Go Fatafat customers.");
      } else {
        showErrorToast("Failed to load Go Fatafat customers.");
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate, pageNumber, pageSize.value, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMarketplaceCustomers();
    }, 350);
    return () => clearTimeout(timer);
  }, [fetchMarketplaceCustomers]);

  useEffect(() => {
    setPageNumber(1);
  }, [search, pageSize.value]);

  const openCustomerDetail = useCallback(async (customerIdentityId) => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_CUSTOMER_BY_ID, {
        params: { CustomerIdentity_ID: customerIdentityId },
      });

      if (response.data?.status) {
        const detail = response.data?.data ?? {};
        setSelectedCustomer(detail.customer ?? null);
        setCustomerAddresses(Array.isArray(detail.addresses) ? detail.addresses : []);
      } else {
        showErrorToast(response.data?.message || "Failed to fetch customer details");
        setSelectedCustomer(null);
        setCustomerAddresses([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 0);
      } else if (err.response?.status === 403) {
        showErrorToast("You are not authorized to view customer details.");
      } else {
        showErrorToast("Failed to load customer details.");
      }
      setSelectedCustomer(null);
      setCustomerAddresses([]);
    } finally {
      setDetailLoading(false);
    }
  }, [dispatch, navigate]);

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item active">Global Customers</li>
          </ol>
          <h1 className="page-header mb-0">Global Customers</h1>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="mb-4">
              <div
                className="card"
                style={{
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: "12px",
                  border: "none",
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-center">
                    <div className="col-xl-4 col-lg-4 col-md-8">
                      <input
                        type="text"
                        className="form-control filter-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, email, phone"
                        style={{ height: "36px" }}
                      />
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-4">
                      <PageSizeDropDownSelection
                        defaultValue={pageSize}
                        onChange={setPageSize}
                        styles={dropdownStyles}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive" style={{ minHeight: "220px", maxHeight: "520px", overflowY: "auto" }}>
              <table className="table table-hover text-nowrap">
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr>
                    <th>ID</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length > 0 ? (
                    customers.map((customer) => (
                      <tr key={customer.customerIdentity_ID}>
                        <td>{customer.customerIdentity_ID}</td>
                        <td>{customer.fullName || "-"}</td>
                        <td>{customer.email || "-"}</td>
                        <td>{customer.phone || "-"}</td>
                        <td>
                          <span className={`badge ${customer.isActive ? "bg-success" : "bg-secondary"}`}>
                            {customer.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          {customer.createdAt ? new Date(customer.createdAt).toLocaleString() : "-"}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openCustomerDetail(customer.customerIdentity_ID)}
                            title="View customer details"
                          >
                            <i className="fa fa-eye" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        {loading ? "Loading customers..." : "No customers found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalRecords={totalRecords}
              pageSize={parseInt(pageSize.value, 10) || 50}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              themeColor={themeColor}
            />
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedCustomer(null);
          setCustomerAddresses([]);
        }}
        title="Customer Detail"
        subtitle={
          selectedCustomer
            ? selectedCustomer.fullName || selectedCustomer.email || "Global Go Fatafat customer"
            : "Global Go Fatafat customer"
        }
        showButton={true}
        showCancelButton={false}
        showSubmitButton={true}
        submitButtonText="Close"
        onSubmit={() => {
          setDetailOpen(false);
          setSelectedCustomer(null);
          setCustomerAddresses([]);
        }}
        modalSize="modal-lg"
        isCentered={true}
        minHeight="280px"
        maxHeight="80vh"
      >
        {detailLoading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div
              className="spinner-border"
              role="status"
              style={{ color: themeColor, width: "2rem", height: "2rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : selectedCustomer ? (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span
                className={`badge ${selectedCustomer.isActive ? "bg-success" : "bg-secondary"}`}
                style={{ fontSize: "12px", padding: "6px 10px" }}
              >
                {selectedCustomer.isActive ? "Active" : "Inactive"}
              </span>
              <span
                className="badge"
                style={{
                  fontSize: "12px",
                  padding: "6px 10px",
                  background: isDarkMode ? "#374151" : "#e5e7eb",
                  color: isDarkMode ? "#f3f4f6" : "#111827",
                }}
              >
                {selectedCustomer.accountDomain || "Go Fatafat"}
              </span>
              <span
                style={{
                  fontSize: "13px",
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                }}
              >
                ID #{selectedCustomer.customerIdentity_ID}
              </span>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <DetailField
                  label="Full Name"
                  value={selectedCustomer.fullName}
                  isDarkMode={isDarkMode}
                />
              </div>
              <div className="col-md-6">
                <DetailField
                  label="Email"
                  value={selectedCustomer.email}
                  isDarkMode={isDarkMode}
                />
              </div>
              <div className="col-md-6">
                <DetailField
                  label="Phone"
                  value={selectedCustomer.phone}
                  isDarkMode={isDarkMode}
                />
              </div>
              <div className="col-md-6">
                <DetailField
                  label="Created"
                  value={formatDateTime(selectedCustomer.createdAt)}
                  isDarkMode={isDarkMode}
                />
              </div>
              <div className="col-md-6">
                <DetailField
                  label="Updated"
                  value={formatDateTime(selectedCustomer.updatedAt)}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0" style={{ fontWeight: 700 }}>
                Addresses
              </h6>
              <span
                style={{
                  fontSize: "12px",
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                }}
              >
                {customerAddresses.length} saved
              </span>
            </div>

            {customerAddresses.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Address</th>
                      <th>Apt</th>
                      <th>Postal Code</th>
                      <th>Label</th>
                      <th>Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerAddresses.map((address) => (
                      <tr key={address.customerIdentityAddress_ID}>
                        <td>{address.customerIdentityAddress_ID}</td>
                        <td>{address.addressLine1 || "-"}</td>
                        <td>{address.aptNo || "-"}</td>
                        <td>{address.postalCode || "-"}</td>
                        <td>{address.label || "-"}</td>
                        <td>
                          <span
                            className={`badge ${
                              address.isDefault ? "bg-success" : "bg-secondary"
                            }`}
                          >
                            {address.isDefault ? "Yes" : "No"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                className="text-center py-4"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px dashed #4b5563" : "1px dashed #d1d5db",
                  background: isDarkMode ? "#111827" : "#f9fafb",
                }}
              >
                <i
                  className="fa fa-map-marker-alt mb-2"
                  style={{
                    fontSize: "22px",
                    color: isDarkMode ? "#6b7280" : "#9ca3af",
                  }}
                />
                <div
                  style={{
                    fontWeight: 600,
                    color: isDarkMode ? "#e5e7eb" : "#374151",
                  }}
                >
                  No addresses found
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: isDarkMode ? "#9ca3af" : "#6b7280",
                  }}
                >
                  This customer has not saved any delivery addresses yet.
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <div style={{ fontWeight: 600 }}>Customer detail not found</div>
            <div
              style={{
                fontSize: "13px",
                color: isDarkMode ? "#9ca3af" : "#6b7280",
              }}
            >
              Unable to load this global customer record.
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default ManageMarketplaceCustomers;
